import { useState, useEffect } from "react";

interface PasswordValidationParams {
  password?: string;
  confirmPassword?: string;
}

interface PasswordValidationResult {
  validLength: boolean;
  hasNumber: boolean;
  upperCase: boolean;
  specialChar: boolean;
  match: boolean;
}

/**
 * Hook to validate password requirements and confirm password matching.
 *
 * Validates the following password requirements:
 * - Minimum 8 characters in length
 * - Contains at least one uppercase letter
 * - Contains at least one number
 * - Contains at least one special character
 * - Matches the confirmation password
 *
 * @param params - Object containing password and confirmPassword
 * @returns Object with boolean flags for each validation rule
 *
 * @example
 * const validation = usePasswordValidation({
 *   password: 'MyPass123!',
 *   confirmPassword: 'MyPass123!'
 * });
 *
 * if (validation.validLength && validation.hasNumber && validation.upperCase &&
 *     validation.specialChar && validation.match) {
 *   // Password is valid
 * }
 */
export const usePasswordValidation = ({
  password = "",
  confirmPassword = ""
}: PasswordValidationParams): PasswordValidationResult => {
  const [validLength, setValidLength] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [upperCase, setUpperCase] = useState<boolean>(false);
  const [specialChar, setSpecialChar] = useState<boolean>(false);
  const [match, setMatch] = useState<boolean>(false);

  useEffect(() => {
    setValidLength(password.length >= 8);
    setUpperCase(password.toLowerCase() !== password);
    setHasNumber(/\d/.test(password));
    setSpecialChar(/[ `!@#$%^&*()_+\-=\]{};':"\\|,.<>?~]/.test(password));
    setMatch(password !== "" && password === confirmPassword);
  }, [password, confirmPassword]);

  return {
    validLength,
    hasNumber,
    upperCase,
    specialChar,
    match
  };
};
